import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

export interface PasswordValidatorOptions {
  minLength?: number;
  maxLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumber?: boolean;
  requireSpecialChar?: boolean;
}

const getPasswordErrorSentence = (errors: ValidationErrors | null): string | null => {
  if (!errors) return null;

  const parts: string[] = [];

  if (errors['minLength']) {
    parts.push(
      $localize`:@@passwordMinLength:minstens ${errors['minLength'].requiredLength} karakters`
    );
  }

  if (errors['maxLength']) {
    parts.push(
      $localize`:@@passwordMaxLength:niet meer dan ${errors['maxLength'].requiredLength} karakters`
    );
  }

  if (errors['requireUppercase']) {
    parts.push($localize`:@@passwordUppercase:één hoofdletter`);
  }

  if (errors['requireLowercase']) {
    parts.push($localize`:@@passwordLowercase:één kleine letter`);
  }

  if (errors['requireNumber']) {
    parts.push($localize`:@@passwordNumber:één cijfer`);
  }

  if (errors['requireSpecialChar']) {
    parts.push($localize`:@@passwordSpecialChar:één speciaal teken`);
  }

  if (parts.length === 0) return null;

  let sentence;

  if (parts.length === 1) {
    sentence = parts[0];
  } else if (parts.length === 2) {
    sentence = `${parts[0]} en ${parts[1]}`;
  } else {
    sentence = `${parts.slice(0, -1).join(', ')} en ${parts[parts.length - 1]}`;
  }

  return $localize`:@@passwordErrorSentence:Het wachtwoord moet minstens ${sentence} bevatten.`;
}


const UPPERCASE_REGEX = /[A-Z]/;
const LOWERCASE_REGEX = /[a-z]/;
const NUMBER_REGEX = /[0-9]/;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*(),.?":{}|<>_\-[\]\\;'`~]/;

export const passwordValidator = (options: PasswordValidatorOptions = {}): ValidatorFn => {
  const validatePasswordConstraint = (
    condition: boolean,
    errorKey: string,
    errorValue: unknown,
    validationErrors: ValidationErrors,
  ): void => {
    if (condition) validationErrors[errorKey] = errorValue;
  };

  return (control: AbstractControl): ValidationErrors | null => {
    const passwordValue = control.value ?? '';
    const validationErrors: ValidationErrors = {};

    if (!passwordValue) return null;

    validatePasswordConstraint(
      !!options.minLength && passwordValue.length < options.minLength,
      'minLength',
      {requiredLength: options.minLength, actualLength: passwordValue.length},
      validationErrors
    );

    validatePasswordConstraint(
      !!options.maxLength && passwordValue.length > options.maxLength,
      'maxLength',
      {requiredLength: options.maxLength, actualLength: passwordValue.length},
      validationErrors
    );

    validatePasswordConstraint(
      !!options.requireUppercase && !UPPERCASE_REGEX.test(passwordValue),
      'requireUppercase',
      true,
      validationErrors
    );

    validatePasswordConstraint(
      !!options.requireLowercase && !LOWERCASE_REGEX.test(passwordValue),
      'requireLowercase',
      true,
      validationErrors
    );

    validatePasswordConstraint(
      !!options.requireNumber && !NUMBER_REGEX.test(passwordValue),
      'requireNumber',
      true,
      validationErrors
    );

    validatePasswordConstraint(
      !!options.requireSpecialChar && !SPECIAL_CHAR_REGEX.test(passwordValue),
      'requireSpecialChar',
      true,
      validationErrors
    );

    return Object.keys(validationErrors).length > 0 ? {
      passwordNotStrongEnough: {errorMessage: getPasswordErrorSentence(validationErrors)}
    } : null;
  };
}
